import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Paper,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Schedule,
  CalendarToday,
  AccessTime,
  Message,
  Edit
} from '@mui/icons-material';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
  onSnapshot,
  increment
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale/es';

const Bookings = () => {
  // Default user ID since authentication is not implemented yet
  const defaultUserId = 'default-user-id';
  
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [isProvider, setIsProvider] = useState(false); // For testing, toggle between user/provider view
  
  const navigate = useNavigate();

  // Load bookings based on user role - with simple queries and manual handling
  useEffect(() => {
    setLoading(true);
    setError(null);
    
    const fetchBookings = async () => {
      try {
        // Use the simplest possible query to avoid index requirements
        const bookingsCol = collection(db, 'bookings');
        const snapshot = await getDocs(bookingsCol);
        
        const allBookings = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          // Filter manually based on user role
          if ((isProvider && data.providerId === defaultUserId) || 
              (!isProvider && data.userId === defaultUserId)) {
            allBookings.push({
              id: doc.id,
              ...data,
              createdAt: data.createdAt?.toDate() || new Date()
            });
          }
        });
        
        // Sort manually
        allBookings.sort((a, b) => b.createdAt - a.createdAt);
        
        setBookings(allBookings);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching bookings:', error);
        setError('Error al cargar las reservas. Por favor intenta más tarde.');
        setLoading(false);
      }
    };
    
    fetchBookings();
    
    // We're not using a real-time listener anymore to avoid index issues
    // If you want real-time updates, you'd need to set up proper Firestore indexes
  }, [isProvider, defaultUserId]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Get filtered bookings based on status
  const getFilteredBookings = () => {
    if (tabValue === 0) {
      return bookings.filter(booking => booking.status === 'pending');
    } else if (tabValue === 1) {
      return bookings.filter(booking => booking.status === 'accepted');
    } else if (tabValue === 2) {
      return bookings.filter(booking => booking.status === 'completed');
    } else {
      return bookings.filter(booking => booking.status === 'cancelled');
    }
  };

  // Open response dialog
  const handleResponseDialog = (booking) => {
    setSelectedBooking(booking);
    setOpenDialog(true);
  };

  // Accept booking
  const handleAcceptBooking = async () => {
    if (!selectedBooking) return;
    
    try {
      // Update booking status
      await updateDoc(doc(db, 'bookings', selectedBooking.id), {
        status: 'accepted',
        responseMessage: responseMessage,
        respondedAt: serverTimestamp()
      });
      
      // Send a message to the user
      await sendBookingMessage(selectedBooking, 'accepted');
      
      setOpenDialog(false);
      setResponseMessage('');
    } catch (error) {
      console.error('Error accepting booking:', error);
      setError('Error al aceptar la reserva');
    }
  };

  // Reject booking
  const handleRejectBooking = async () => {
    if (!selectedBooking) return;
    
    try {
      // Update booking status
      await updateDoc(doc(db, 'bookings', selectedBooking.id), {
        status: 'cancelled',
        responseMessage: responseMessage,
        respondedAt: serverTimestamp()
      });
      
      // Send a message to the user
      await sendBookingMessage(selectedBooking, 'cancelled');
      
      setOpenDialog(false);
      setResponseMessage('');
    } catch (error) {
      console.error('Error rejecting booking:', error);
      setError('Error al rechazar la reserva');
    }
  };

  // Send a message about the booking
  const sendBookingMessage = async (booking, status) => {
    try {
      // Check if a conversation exists
      const conversationsQuery = query(
        collection(db, 'conversations'),
        where('userId', '==', booking.userId),
        where('providerId', '==', booking.providerId)
      );
      
      const conversationSnapshot = await getDocs(conversationsQuery);
      let conversationId;
      
      if (!conversationSnapshot.empty) {
        conversationId = conversationSnapshot.docs[0].id;
      } else {
        // Create a new conversation if it doesn't exist
        // This should rarely happen as booking creation typically creates a conversation
        const providerDoc = await getDoc(doc(db, 'providers', booking.providerId));
        const provider = providerDoc.exists() ? providerDoc.data() : { name: 'Proveedor' };
        
        const newConversationRef = await addDoc(collection(db, 'conversations'), {
          userId: booking.userId,
          providerId: booking.providerId,
          userName: booking.userName || 'Cliente',
          providerName: provider.name || 'Proveedor',
          lastMessage: status === 'accepted' 
            ? `Reserva aceptada: ${booking.serviceType}`
            : `Reserva cancelada: ${booking.serviceType}`,
          lastMessageTimestamp: serverTimestamp(),
          createdAt: serverTimestamp(),
          userUnreadCount: 1,
          providerUnreadCount: 0
        });
        
        conversationId = newConversationRef.id;
      }
      
      // Add message to the conversation
      const messageContent = status === 'accepted'
        ? `He aceptado tu reserva para ${booking.serviceType} el ${format(new Date(booking.date), 'PPP', { locale: es })} a las ${booking.time}. ${responseMessage}`
        : `No puedo atender tu reserva para ${booking.serviceType} el ${format(new Date(booking.date), 'PPP', { locale: es })} a las ${booking.time}. ${responseMessage}`;
      
      // Create message in Firestore
      await addDoc(collection(db, 'messages'), {
        conversationId: conversationId,
        senderId: booking.providerId,
        recipientId: booking.userId,
        content: messageContent,
        timestamp: serverTimestamp(),
        isRead: false,
        type: 'booking-response',
        relatedBookingId: booking.id,
        bookingStatus: status
      });
      
      // Update conversation with new message and increment unread count
      await updateDoc(doc(db, 'conversations', conversationId), {
        lastMessage: status === 'accepted' 
          ? `Reserva aceptada: ${booking.serviceType}`
          : `Reserva cancelada: ${booking.serviceType}`,
        lastMessageTimestamp: serverTimestamp(),
        userUnreadCount: increment(1)
      });
      
      // Create a notification entry for the booking response
      try {
        // Add a notification document to store the event for reference
        // This can be used to build a notifications history if needed
        await addDoc(collection(db, 'notifications'), {
          userId: booking.userId,
          providerId: booking.providerId,
          title: status === 'accepted' 
            ? `Reserva aceptada: ${booking.serviceType}` 
            : `Reserva cancelada: ${booking.serviceType}`,
          body: messageContent,
          type: 'booking-response',
          status: status,
          bookingId: booking.id,
          conversationId: conversationId,
          isRead: false,
          timestamp: serverTimestamp()
        });
      } catch (error) {
        console.error('Error creating notification entry:', error);
      }
      
    } catch (error) {
      console.error('Error sending booking message:', error);
    }
  };

  // Get status chip for a booking
  const getStatusChip = (status) => {
    switch (status) {
      case 'pending':
        return <Chip icon={<Schedule />} label="Pendiente" color="warning" />;
      case 'accepted':
        return <Chip icon={<CheckCircle />} label="Aceptada" color="success" />;
      case 'completed':
        return <Chip icon={<CheckCircle />} label="Completada" color="primary" />;
      case 'cancelled':
        return <Chip icon={<Cancel />} label="Cancelada" color="error" />;
      default:
        return <Chip label={status} color="default" />;
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return format(date, 'PPP', { locale: es });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  // View conversation related to a booking
  const handleViewConversation = async (booking) => {
    try {
      // Find conversation between user and provider
      const conversationsQuery = query(
        collection(db, 'conversations'),
        where('userId', '==', booking.userId),
        where('providerId', '==', booking.providerId)
      );
      
      const conversationSnapshot = await getDocs(conversationsQuery);
      
      if (!conversationSnapshot.empty) {
        const conversationId = conversationSnapshot.docs[0].id;
        navigate(`/messages?conversation=${conversationId}`);
      } else {
        console.error('No conversation found for this booking');
        setError('No se encontró la conversación para esta reserva');
      }
    } catch (error) {
      console.error('Error finding conversation:', error);
      setError('Error al buscar la conversación');
    }
  };

  // For demo purposes, toggle between user and provider views
  const toggleUserRole = () => {
    setIsProvider(!isProvider);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: '#2C3E50' }}>
          Mis Reservas
        </Typography>
        
        {/* Demo toggle between user/provider roles */}
        <Button
          variant="outlined"
          onClick={toggleUserRole}
          sx={{ borderRadius: '8px' }}
        >
          Cambiar a {isProvider ? 'Cliente' : 'Proveedor'}
        </Button>
      </Box>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        Estás viendo las reservas como {isProvider ? 'proveedor' : 'cliente'}. 
        {isProvider 
          ? ' Puedes aceptar o rechazar las solicitudes de reserva.'
          : ' Puedes ver el estado de tus reservas solicitadas.'}
      </Alert>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Paper sx={{ borderRadius: '12px', overflow: 'hidden', mb: 4 }}>
        {/* Tabs for filtering bookings */}
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: '1px solid #eee',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.9rem',
              minHeight: '48px'
            },
            '& .Mui-selected': {
              color: '#FFD700',
              fontWeight: 600
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#FFD700'
            }
          }}
        >
          <Tab label="Pendientes" />
          <Tab label="Aceptadas" />
          <Tab label="Completadas" />
          <Tab label="Canceladas" />
        </Tabs>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : getFilteredBookings().length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="textSecondary">
              No tienes reservas {
                tabValue === 0 ? 'pendientes' :
                tabValue === 1 ? 'aceptadas' :
                tabValue === 2 ? 'completadas' : 'canceladas'
              }
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {getFilteredBookings().map((booking) => (
                <Grid item xs={12} md={6} key={booking.id}>
                  <Card 
                    sx={{ 
                      borderRadius: '10px', 
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <CardContent sx={{ p: 3, flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#2C3E50' }}>
                          {booking.serviceType}
                        </Typography>
                        {getStatusChip(booking.status)}
                      </Box>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <CalendarToday fontSize="small" sx={{ color: '#999' }} />
                          <Typography variant="body2">
                            {formatDate(booking.date)}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <AccessTime fontSize="small" sx={{ color: '#999' }} />
                          <Typography variant="body2">
                            {booking.time} ({booking.duration} {booking.duration === '1' ? 'hora' : 'horas'})
                          </Typography>
                        </Box>
                        
                        <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
                          {booking.description}
                        </Typography>
                        
                        {booking.responseMessage && (
                          <Box sx={{ mt: 2, p: 2, bgcolor: '#f9f9f9', borderRadius: '8px' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                              Respuesta del {isProvider ? 'cliente' : 'proveedor'}:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#555' }}>
                              {booking.responseMessage}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'flex-end', 
                      p: 2, 
                      borderTop: '1px solid #eee',
                      gap: 1
                    }}>
                      <Button
                        startIcon={<Message />}
                        variant="outlined"
                        size="small"
                        onClick={() => handleViewConversation(booking)}
                        sx={{ 
                          borderColor: '#e0e0e0',
                          color: '#666',
                          '&:hover': { borderColor: '#FFD700', bgcolor: 'rgba(255,215,0,0.04)' }
                        }}
                      >
                        Ver Mensajes
                      </Button>
                      
                      {isProvider && booking.status === 'pending' && (
                        <Button
                          startIcon={<Edit />}
                          variant="contained"
                          size="small"
                          onClick={() => handleResponseDialog(booking)}
                          sx={{ 
                            bgcolor: '#FFD700',
                            color: '#000',
                            '&:hover': { bgcolor: '#FFC700' }
                          }}
                        >
                          Responder
                        </Button>
                      )}
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Paper>
      
      {/* Response Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ 
          fontWeight: 600, 
          color: '#2C3E50',
          borderBottom: '1px solid #eee',
          pb: 2
        }}>
          Responder a la solicitud
        </DialogTitle>
        
        <DialogContent sx={{ py: 3, px: 3 }}>
          {selectedBooking && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Servicio: {selectedBooking.serviceType}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 4 }}>
                <Typography variant="body2">
                  Fecha: {formatDate(selectedBooking.date)}
                </Typography>
                
                <Typography variant="body2">
                  Hora: {selectedBooking.time}
                </Typography>
              </Box>
              
              <TextField
                label="Mensaje de respuesta"
                multiline
                rows={4}
                fullWidth
                value={responseMessage}
                onChange={(e) => setResponseMessage(e.target.value)}
                placeholder="Escribe un mensaje para el cliente..."
                variant="outlined"
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eee' }}>
          <Button 
            onClick={() => setOpenDialog(false)}
            sx={{ color: '#666' }}
          >
            Cancelar
          </Button>
          
          <Button 
            onClick={handleRejectBooking}
            sx={{ 
              color: '#f44336',
              '&:hover': { bgcolor: 'rgba(244,67,54,0.04)' }
            }}
          >
            Rechazar
          </Button>
          
          <Button 
            variant="contained"
            onClick={handleAcceptBooking}
            sx={{ 
              bgcolor: '#FFD700',
              color: '#000',
              '&:hover': { bgcolor: '#FFC700' }
            }}
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Bookings; 